import { Box, BoxProps, Button, OSKIcon, OSKIconType, OSKThemeType, Typography } from 'oskcomponents';
import React from 'react';
import toast from 'react-hot-toast';
import { useTheme } from 'styled-components';
import { CollapsableGroup, PurchasedChip } from '~/atoms';

export type SceneGroupIcon = 'all' | 'some' | 'none' | 'hidden';

export type SceneGroupProps = {
    /** Children elements to render inside this group */
    children?: React.ReactNode;
    /** The id of the task this group represents. (Used for the title.) */
    taskId: string;
    /** The number of scenes in this group that have been purchased */
    purchasedCount?: number;
    /** An optional element to display underneath the task id */
    subtitle?: React.ReactNode;
    /** The icon type to show. all | some | none */
    selectedIcon: SceneGroupIcon;
    /** An optional override for the icon we display in the capture group. */
    icon?: OSKIconType;
    /** Method to invoke when the icon is clicked */
    onIconClick?: () => void;
    /** Method that's invoked when the expand status is changing */
    onExpandChange?: (isExpanded: boolean) => void;
    /** A method that's called before expansion is finalized.
     * Returns a promise that resolves when the operation is complete. */
    beforeExpand?: () => Promise<void>;
    /** If true, the group will start expanded */
    defaultExpanded?: boolean;
} & Omit<BoxProps, 'ref'>;

export const SceneGroup = ({
    children,
    icon,
    selectedIcon,
    onIconClick,
    onExpandChange,
    beforeExpand,
    defaultExpanded,
    taskId,
    purchasedCount = 0,
    subtitle,
    style,
    ...props
}: SceneGroupProps) => {
    const theme = useTheme() as OSKThemeType;

    return (
        <CollapsableGroup
            style={{
                borderRadius: '4px',
                border: `1px solid ${theme.colors.black200}`,
                backgroundColor: theme.colors.white,
                overflowY: 'auto',
                overflowX: 'hidden',
                width: '100%',
                minWidth: 0,
                ...style,
            }}
            onExpandChange={onExpandChange}
            beforeExpand={beforeExpand}
            defaultExpanded={defaultExpanded}
            title={
                <Box
                    style={{
                        padding: '14px 0px',
                        flex: '0 1 auto',
                        minWidth: 0,
                    }}
                >
                    {selectedIcon !== 'hidden' ? (
                        <Box
                            center="all"
                            w={40}
                            p={12}
                            onClick={(evt) => {
                                evt.preventDefault();
                                evt.stopPropagation();
                                onIconClick && onIconClick();
                            }}
                        >
                            <OSKIcon
                                fill={theme.colors.black600}
                                code={
                                    selectedIcon === 'all'
                                        ? 'check-square'
                                        : selectedIcon === 'some'
                                        ? 'dash-square'
                                        : 'reg-square'
                                }
                                scale={150}
                                cursor="pointer"
                            />
                        </Box>
                    ) : null}
                    <Box
                        center="vertical"
                        style={{
                            paddingLeft: selectedIcon !== 'hidden' ? '2px' : '8px',
                            minWidth: 0,
                            flex: '0 1 auto',
                        }}
                    >
                        <OSKIcon code={icon ?? 'footprint'} strokeWidth={1} width={40} />
                        <Box
                            style={{
                                minWidth: 0,
                                textOverflow: 'ellipsis',
                                paddingLeft: '12px',
                            }}
                            col
                            center="vertical"
                        >
                            <Box center="vertical">
                                <Typography
                                    variant="body3"
                                    strong
                                    style={{
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        minWidth: 0,
                                        width: '150px',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={taskId}
                                >
                                    {taskId}
                                </Typography>
                                <Button
                                    variant="contrast"
                                    style={{ padding: '8px' }}
                                    onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        navigator.clipboard.writeText(taskId);
                                        toast.success(`Copied Task ID to clipboard!`);
                                    }}
                                >
                                    <OSKIcon code="copy-modern" />
                                </Button>
                                {purchasedCount > 0 && <PurchasedChip value={purchasedCount.toString()} />}
                            </Box>

                            {subtitle}
                        </Box>
                    </Box>
                </Box>
            }
            {...props}
        >
            {children}
        </CollapsableGroup>
    );
};
